import { useState, useEffect } from "react";
import { format, startOfWeek, addDays, isSameDay, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BookingRequestCard } from "./BookingRequestCard";
import { formatTime } from "@/lib/utils";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  duration_minutes: number;
  status: string;
  price: number;
  user_id: string;
  coach_id?: string;
  video_conference_link?: string;
  proposed_datetime?: string | null;
  coach_feedback?: string;
  denied_by?: string[];
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface BlockedTime {
  id: string;
  blocked_date: string;
  start_time: string;
  end_time: string;
  reason: string | null;
}

interface CoachCalendarProps {
  bookings: Booking[];
  onUpdate: () => void;
  isAdminView?: boolean;
  coachId?: string; // For filtering denied bookings and admin actions
}

const timeSlots = Array.from({ length: 15 }, (_, i) => i + 8);

export const CoachCalendar = ({ bookings, onUpdate, isAdminView = false, coachId }: CoachCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  
  // Filter bookings based on view type
  // Admin view: show all bookings but exclude those denied by this coach
  // Coach view: show confirmed, cancelled, proposed, and pending (not denied by this coach)
  const calendarBookings = bookings.filter(b => {
    // If this coach has denied this booking, don't show it
    if (coachId && b.denied_by?.includes(coachId)) {
      return false;
    }
    
    // In admin view, show all other bookings
    if (isAdminView) {
      return true;
    }
    
    // In coach view, show confirmed, cancelled, proposed, and pending bookings
    return b.status === "confirmed" || b.status === "cancelled" || b.status === "proposed" || b.status === "pending";
  });

  useEffect(() => {
    fetchBlockedTimes();
  }, []);

  const fetchBlockedTimes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("blocked_times")
        .select("*")
        .eq("coach_id", user.id);

      if (error) throw error;
      setBlockedTimes(data || []);
    } catch (error) {
      console.error("Error fetching blocked times:", error);
    }
  };

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getBookingsForDayAndHour = (day: Date, hour: number) => {
    return calendarBookings.filter((booking) => {
      // Use proposed datetime for bookings with status "proposed"
      const displayDatetime = booking.status === "proposed" && booking.proposed_datetime 
        ? booking.proposed_datetime 
        : booking.booking_datetime;
      
      const timezone = booking.booking_timezone || "America/New_York";
      
      // Convert UTC timestamp to the booking's timezone
      const bookingDateInTimezone = toZonedTime(new Date(displayDatetime), timezone);
      
      // Check if same day
      if (!isSameDay(bookingDateInTimezone, day)) return false;
      
      // Get hour in the booking's timezone
      const bookingHour = bookingDateInTimezone.getHours();
      return bookingHour === hour;
    });
  };

  const isTimeBlocked = (day: Date, hour: number) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return blockedTimes.some((blocked) => {
      if (blocked.blocked_date !== dateStr) return false;
      const startHour = parseInt(blocked.start_time.split(":")[0]);
      const endHour = parseInt(blocked.end_time.split(":")[0]);
      return hour >= startHour && hour < endHour;
    });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {format(weekStart, "MMMM yyyy")}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(addDays(currentDate, -7))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(addDays(currentDate, 7))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden bg-card">
          <div className="grid grid-cols-8 border-b bg-muted/50">
            <div className="p-2 text-sm font-medium text-muted-foreground border-r">
              Time
            </div>
            {weekDays.map((day) => (
              <div
                key={day.toISOString()}
                className={`p-2 text-center border-r last:border-r-0 ${
                  isSameDay(day, new Date()) ? "bg-primary/10 font-bold" : ""
                }`}
              >
                <div className="text-xs text-muted-foreground">
                  {format(day, "EEE")}
                </div>
                <div
                  className={`text-lg ${
                    isSameDay(day, new Date()) ? "text-primary" : ""
                  }`}
                >
                  {format(day, "d")}
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-y-auto max-h-[600px]">
            {timeSlots.map((hour) => (
              <div key={hour} className="grid grid-cols-8 border-b last:border-b-0">
                <div className="p-2 text-sm text-muted-foreground border-r bg-muted/30">
                  {format(new Date().setHours(hour, 0, 0, 0), "h:mm a")}
                </div>

                {weekDays.map((day) => {
                  const dayBookings = getBookingsForDayAndHour(day, hour);
                  const isBlocked = isTimeBlocked(day, hour);
                  return (
                    <div
                      key={`${day.toISOString()}-${hour}`}
                      className={`min-h-[80px] p-1 border-r last:border-r-0 transition-colors ${
                        isBlocked
                          ? "bg-destructive/10 hover:bg-destructive/20"
                          : "hover:bg-accent/50"
                      }`}
                    >
                      {isBlocked && (
                        <div className="flex items-center gap-1 text-xs text-destructive font-medium mb-1">
                          <Ban className="h-3 w-3" />
                          Blocked
                        </div>
                      )}
                      {dayBookings.map((booking) => {
                        const displayDatetime = booking.status === "proposed" && booking.proposed_datetime 
                          ? booking.proposed_datetime 
                          : booking.booking_datetime;
                        const displayTime = format(new Date(displayDatetime), "HH:mm");
                        
                        return (
                          <Card
                            key={booking.id}
                            className={`p-2 mb-1 cursor-pointer hover:shadow-md transition-shadow ${
                              booking.status === "confirmed"
                                ? "bg-primary/10 border-primary/50"
                                : booking.status === "cancelled"
                                ? "bg-destructive/10 border-destructive/50"
                                : booking.status === "proposed"
                                ? "bg-amber-500/10 border-amber-500/50"
                                : "bg-yellow-500/10 border-yellow-500/50"
                            }`}
                            onClick={() => setSelectedBooking(booking)}
                          >
                            <div className="text-xs font-semibold truncate">
                              {booking.profiles?.full_name || "Unknown"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatTime(displayTime)} ({booking.duration_minutes}m)
                            </div>
                            <Badge
                              variant={
                                booking.status === "confirmed"
                                  ? "default"
                                  : booking.status === "cancelled"
                                  ? "destructive"
                                  : "secondary"
                              }
                              className="text-xs mt-1"
                            >
                              {booking.status}
                            </Badge>
                          </Card>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <BookingRequestCard
              booking={selectedBooking}
              showActions={true}
              onUpdate={() => {
                onUpdate();
                setSelectedBooking(null);
              }}
              overrideCoachId={isAdminView ? coachId : undefined}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
